import { cloneDeep, groupBy, orderBy } from 'lodash';

import { ASSET_TYPE, type AssetType } from 'src/data/asset';
import {
  type GenerationRequest,
  isSectionRecomposeResult,
  isSuggestGenerationResult,
  type SuggestionRequest,
} from 'src/data/generation';
import {
  GENERATION_TOOL,
  type GenerationTool,
  getMultiPromptSubtools,
  getToolClassifiedMixtureSubGenerationTools,
  getToolDefaultAssetType,
  getToolMixtureSubGenerationTools,
  isMixtureTool,
} from 'src/data/generationTool';
import type { ID } from 'src/lib/collection';

interface Options {
  removeEmpty?: boolean;
}

/**
 * Sorts and regroups short form generation results based on their creation date and original variation IDs.
 * @param results - Array of generation results to be sorted.
 * @param options - Options for filtering and sorting.
 * @param [options.removeEmpty=false] - If true, filters out results with no variations.
 * @returns - Sorted and regrouped generation results.
 */
export const sortShortFormGenerationResults = (
  results: GenerationRequest[],
  options?: Options
): GenerationRequest[] => {
  const filteredResults = options?.removeEmpty
    ? results.filter((result) => result.variations.length > 0)
    : results;

  // Separate section recompose results (content improver by section) from other results
  const sectionRecomposeResults = filteredResults.filter(isSectionRecomposeResult);
  const nonSectionRecomposeResults = filteredResults.filter(
    (result) => !isSectionRecomposeResult(result)
  );

  // Process section recompose results separately - group by chainId and groupIdx
  const groupedSectionResults = groupSectionRecomposeResults(sectionRecomposeResults);

  // These are results with original/suggest variations only
  const originalResults = nonSectionRecomposeResults
    .filter((result) => !result.originalVariationId)
    .map((result) => {
      // remove mixed variations from original variations
      const variations = result.variations.filter((variation) => !variation.originalVariationId);
      return { ...result, variations };
    });

  const groupedNonSectionResults = sortChainedResults(originalResults);

  // some results have internal originalVariationId (only at the variation level) for some of their variations,
  // these will be added as nested variations after removing the variations without originalVariationId
  const resultsWithInternalOriginalVariationId = cloneDeep(nonSectionRecomposeResults)
    .filter((result) => {
      return !result.originalVariationId && result?.variations?.some((v) => v.originalVariationId);
    })
    .map((item) => {
      item.variations = item.variations.filter((v) => v.originalVariationId);
      item.originalVariationId = item.variations[0].originalVariationId;
      return item;
    });

  // These will be added as nested variations to the original id variation
  const descendantResults = nonSectionRecomposeResults
    .filter((result) => result.originalVariationId)
    .concat(resultsWithInternalOriginalVariationId);
  const migratedDescendantResults = migrateDescendantVariations(descendantResults);

  const finalSorted = groupedNonSectionResults;

  // we want to keep order for groupedNonSectionResults and inject groupedSectionResults into it based on createdAt (both are sorted desc already)
  // ("sections" already sorted by "groupIdx" desc)
  groupedSectionResults.forEach((sectionResult) => {
    const insertIndex = finalSorted.findIndex(
      (result) => result.createdAt < sectionResult.createdAt
    );
    if (insertIndex === -1) {
      finalSorted.push(sectionResult);
    } else {
      finalSorted.splice(insertIndex, 0, sectionResult);
    }
  });

  finalSorted.forEach((finalResult) => {
    // add order idx to mixture tool variations for grouping
    if (
      isMixtureTool(finalResult.generationTool) &&
      finalResult.generationTool !== GENERATION_TOOL.googleRSA
    ) {
      finalResult = mixtureResultsWithOrderIdx(finalResult);
    } else {
      finalResult = sortVariations(finalResult);
    }

    // add nested variations to results
    migratedDescendantResults.forEach((descendantResult) => {
      if (!descendantResult.originalVariationId) {
        return;
      }

      if (finalResult.variations.map((v) => v.id).includes(descendantResult.originalVariationId)) {
        const originalVariation = finalResult.variations.find(
          (v) => v.id === descendantResult.originalVariationId
        );
        if (!originalVariation) {
          return;
        }

        const currentVariations = originalVariation?.variations || [];
        originalVariation.variations = [...currentVariations, ...descendantResult.variations];
      }
    });
  });

  return finalSorted;
};

const sortChainedResults = (generationResults: GenerationRequest[]): GenerationRequest[] => {
  const finalSorted: GenerationRequest[] = [];

  if (!generationResults || !generationResults.length) {
    return finalSorted;
  }

  const resultsWithoutChainId = generationResults.filter(
    (result) => !isSuggestGenerationResult(result) || !result.chainId
  );
  let sortedWithoutChainId = orderBy(resultsWithoutChainId, ['createdAt', 'id'], ['desc', 'desc']);

  const resultsWithChainId: SuggestionRequest[] = generationResults
    .filter((result) => isSuggestGenerationResult(result))
    .filter((result) => result.chainId);
  const sortedWithChainId = orderBy(resultsWithChainId, ['chainId', 'createdAt'], ['desc', 'desc']);

  if (sortedWithChainId.length > 0) {
    // first, need to group results by chain id, while also keeping track of created at edges. Each chainId should have a single createdAt edge.

    const createdAtEdges: Record<string, string> = {};
    const chainIdSubToolResults: Record<string, SuggestionRequest[]> = {};
    const chainIdGenerationTool: Record<string, GenerationTool> = {};
    sortedWithChainId.forEach((res) => {
      const chainId = String(res.chainId);
      const chainTool = res.chainGenerationTool;
      if (!chainId || !chainTool) {
        return;
      }

      if (!chainIdSubToolResults[chainId]) {
        chainIdSubToolResults[chainId] = [];
        chainIdGenerationTool[chainId] = chainTool;
      }
      chainIdSubToolResults[chainId].push(res);

      if (!Object.keys(createdAtEdges).includes(chainId)) {
        createdAtEdges[chainId] = res.createdAt;
      }
    });

    // sort the subTool results by blockIds or fall back the order of the subTools in the getMultiPromptSubtools
    Object.keys(chainIdSubToolResults).forEach((res) => {
      chainIdSubToolResults[res] = chainIdSubToolResults[res].sort((a, b) => {
        if (a.blockId && b.blockId) {
          return a.blockId - b.blockId;
        }
        const promptTools = getMultiPromptSubtools(chainIdGenerationTool[res]);
        if (!promptTools) {
          return 0;
        }

        const tools = Object.values(promptTools);
        return tools.indexOf(a.generationTool) - tools.indexOf(b.generationTool);
      });
    });

    // iterate over createdAtEdges (newest first), and for each createdAtEdge, add all results that are greater than that createdAtEdge,
    // else add all subTool results for that chainId and delete them from the chainIdSubToolResults object
    Object.keys(createdAtEdges)
      .reverse()
      .forEach((chainId) => {
        sortedWithoutChainId = sortedWithoutChainId.filter((res) => {
          if (res.createdAt > createdAtEdges[chainId]) {
            finalSorted.push(res);
            return false;
          }

          if (chainIdSubToolResults[chainId]) {
            chainIdSubToolResults[chainId].forEach((res) => {
              finalSorted.push(res);
            });
            delete chainIdSubToolResults[chainId];
          }
          return true;
        });
      });

    // add any remaining subTool results to the end of the list
    Object.keys(chainIdSubToolResults)
      .reverse()
      .forEach((chainId) => {
        chainIdSubToolResults[chainId].forEach((res) => {
          finalSorted.push(res);
        });
      });

    // add any remaining non subTool results to the end of the list
    finalSorted.push(...sortedWithoutChainId);
  } else {
    finalSorted.push(...sortedWithoutChainId);
  }

  return finalSorted;
};

const migrateDescendantVariations = (
  resultsWithOriginalVariationId: GenerationRequest[]
): GenerationRequest[] => {
  // sort from newest to oldest, so it is easier to move newer variations into older ones
  const sortedResults = orderBy(
    resultsWithOriginalVariationId,
    ['createdAt', 'id'],
    ['desc', 'desc']
  );

  // create a map of "variation" IDs to "result" IDs
  const variationIdToResultId = sortedResults.reduce((acc: Record<ID, ID>, result) => {
    result.variations.forEach((v) => {
      acc[v.id] = result.id;
    });
    return acc;
  }, {});

  // iterate over the "sortedResults" and add the variations to the original variation
  sortedResults.forEach((result) => {
    if (!result.originalVariationId) {
      return;
    }

    const originalResultId = variationIdToResultId[result.originalVariationId];
    if (!originalResultId) {
      return;
    }

    const originalResult = sortedResults.find((r) => r.id === originalResultId);
    if (!originalResult) {
      return;
    }

    const originalVariation = originalResult.variations.find(
      (v) => v.id === result.originalVariationId
    );
    if (!originalVariation) {
      return;
    }

    const currentVariations = originalVariation?.variations || [];
    originalVariation.variations = [...currentVariations, ...result.variations];
    originalResult.variations = originalResult.variations.map((r) => {
      return { ...r, groupIdx: [Number(originalVariation.id)] };
    });
  });

  return sortedResults;
};

const mixtureResultsWithOrderIdx = (result: GenerationRequest) => {
  const sorted = result.variations.sort((a, b) => {
    // Puts an image in the end of the list
    if (a.assetType === ASSET_TYPE.image && b.assetType !== ASSET_TYPE.image) return 1;
    if (a.assetType !== ASSET_TYPE.image && b.assetType === ASSET_TYPE.image) return -1;

    // Sorts by order of assetTypes in tool props and then by score
    const genTool = a.generationTool;
    const mixtureTools = [
      ...getToolMixtureSubGenerationTools(genTool),
      ...getToolClassifiedMixtureSubGenerationTools(genTool),
    ];
    const assetTypes = mixtureTools.map((tool) => getToolDefaultAssetType(tool));
    const rank = (assetType: AssetType | undefined) => {
      const idx = assetTypes.indexOf(assetType);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return rank(a.assetType) - rank(b.assetType) || b.score - a.score;
  });

  // add groupOrderIdx to variations
  const groupIdxToOrderIdx: Record<string, number> = {};

  const sortedWithGroupOrderIdx = sorted.map((variation, idx) => {
    const setGroupIdx = !Object.keys(groupIdxToOrderIdx).includes(variation.groupIdx[0].toString());
    if (setGroupIdx) {
      groupIdxToOrderIdx[variation.groupIdx[0]] = idx;
      variation.groupOrderIdx = [idx];
    } else {
      variation.groupOrderIdx = [groupIdxToOrderIdx[variation.groupIdx[0]]];
    }
    return variation;
  });

  return { ...result, variations: sortedWithGroupOrderIdx };
};

const sortVariations = (result: GenerationRequest) => {
  const sorted = result.variations.sort((a, b) => {
    // Puts an image in the end of the list
    if (a.assetType === ASSET_TYPE.image && b.assetType !== ASSET_TYPE.image) return 1;
    if (a.assetType !== ASSET_TYPE.image && b.assetType === ASSET_TYPE.image) return -1;

    return 0;
  });

  return { ...result, variations: sorted };
};

/**
 * Groups section recompose results by chainId and then by groupIdx.
 * Variations are paired by their array position across results in the same chain.
 * Each group becomes a separate generation result with ID `{resultId}-{groupIdx}`.
 */
const groupSectionRecomposeResults = (
  sectionRecomposeResults: GenerationRequest[]
): GenerationRequest[] => {
  if (sectionRecomposeResults.length === 0) {
    return [];
  }

  // group by generation "chains"
  const resultsByChainId = groupBy(sectionRecomposeResults, 'chainId');

  const groupedResults: GenerationRequest[] = [];

  Object.entries(resultsByChainId).forEach(([, chainResults]) => {
    const firstResultCreatedAt = chainResults[0].createdAt;

    // sort by generation "blocks" order and update some results contents
    const sortedResults = orderBy(chainResults, ['blockId'], ['asc']);
    const updatedResults = sortedResults.map((result) => ({
      ...result,
      // sort variations by creation order
      variations: orderBy(result.variations, ['id'], ['asc']),
      // override createdAt to be the same for all results in the same chain, so when we will be merging "sections" and non-"sections" results,
      // "sections" sorting will be preserved (we might have variance in "createdAt" in the same chain)
      createdAt: firstResultCreatedAt,
    }));

    groupedResults.push(...updatedResults);
  });

  return groupedResults;
};
